import { useT } from "@/i18n";
import { PerformanceFlame } from "./PerformanceFlame";
import {
  getPerformanceMessage,
  getPerformanceSubMessage,
  getPerformanceVisual,
  hexToRgba,
} from "./reportTokens";

/**
 * The report's hero: how the student has scored recently, as a flame and one number on the app's
 * indigo — the same flame the student sees, heat and all.
 */
export function PerformanceCard({
  recentPerformance,
}: { recentPerformance: number | null | undefined }) {
  // No scores yet reads as 0%, not as an empty card — the card keeps one shape always.
  const t = useT();
  const percent = Math.round(recentPerformance ?? 0);
  const { glow, glowStrength } = getPerformanceVisual(percent);

  return (
    <div className="relative overflow-hidden rounded-card bg-gradient-to-b from-[#1B1B5C] to-[#2626A5] px-6 py-5 text-center shadow-app-card">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${hexToRgba(glow, 0.22 * glowStrength)} 0%, ${hexToRgba(glow, 0)} 85%)`,
        }}
      />

      <div className="relative">
        <p className="app-label text-white/55">{t["report.performance.title"]}</p>

        <div className="mt-3 flex items-center justify-center gap-0.5">
          <PerformanceFlame percent={percent} />
          <p
            className="font-kumbh text-5xl font-extrabold tabular-nums text-white"
            style={{ textShadow: `0 0 24px ${glow}` }}
          >
            {percent}%
          </p>
        </div>

        {/* Headline and caption: the pair separates on weight, size and ink at once, so the
            sub-line reads as an aside rather than a second message. */}
        <p className="mt-5 font-kumbh text-[22px] font-extrabold leading-7 tracking-[-0.3px] text-white">
          {getPerformanceMessage(t, percent)}
        </p>
        <p className="mt-1 font-kumbh text-[13px] leading-[18px] text-white/60">
          {getPerformanceSubMessage(t, percent)}
        </p>
      </div>
    </div>
  );
}
